import { useMemo } from 'react';
import { taskStepOf, type TaskStep } from '@d-orca/shared/client/activities';
import type { ISchedulerTask, SiteId, TaskId } from '@interfaces/IScheduler';
import { useSchedulerSite, useSchedulerTask } from './useSchedulerStore';
import { useTaskLadderOrPlanned } from './useTaskLadderOrPlanned';
import { leafDescendants } from '../lib/commitReadiness';

// The task's STEP — the one action its position on the ladder calls for — as
// data. The drawer tells it twice (TaskPanel/TaskStep.tsx): the footer's
// buttons, and the hint under the promise rows. One reading serves both; the
// reading itself (`taskStepOf`) is shared with the mobile sheet.

export type { TaskStep };

interface ITaskStepState {
  task: ISchedulerTask;
  /** Leaf descendants — non-empty exactly when the task is a parent. */
  leaves: readonly ISchedulerTask[];
  step: TaskStep;
}

/** The task, its leaves and its step — `undefined` until the task resolves. */
export function useTaskStep(siteId: SiteId, taskId: TaskId): ITaskStepState | undefined {
  const site = useSchedulerSite(siteId);
  const task = useSchedulerTask(siteId, taskId);
  const ladder = useTaskLadderOrPlanned(task, taskId);
  // The walk indexes the whole plan — once per plan change, not per render.
  const siteTasks = site?.tasks;
  const leaves = useMemo(() => leafDescendants(siteTasks ?? [], taskId), [siteTasks, taskId]);
  if (!task) return undefined;
  return { task, leaves, step: taskStepOf(task, ladder.rung, leaves) };
}
